import { getFoodCourtMenuPublicUrl } from './menuPathUtils';

export type FoodCourtMenuMetadata = {
  storage?: 'local' | 's3';
  key?: string;
  fileName?: string;
  fileType?: string;
  url?: string;
};

const MENU_URL_PATTERN = /\.(pdf|png|jpe?g|webp)(\?|#|$)/i;

/** Resolve the URL used to display a menu (local disk, S3, or external). */
export function resolveFoodCourtMenuViewUrl(menu: FoodCourtMenuMetadata): string | null {
  if (menu.url) {
    return menu.url;
  }
  if (menu.key && (menu.storage === 'local' || !menu.storage)) {
    return getFoodCourtMenuPublicUrl(menu.key);
  }
  return null;
}

/** True when menu file is on S3 and needs a signed download URL. */
export function isS3FoodCourtMenu(menu: FoodCourtMenuMetadata): boolean {
  return menu.storage === 's3' && !!menu.key;
}

export function isMenuMimeType(fileType: string): boolean {
  return (
    fileType === 'application/pdf' ||
    fileType === 'image/jpeg' ||
    fileType === 'image/png' ||
    fileType === 'image/webp'
  );
}

export function isPdfMenu(menu: FoodCourtMenuMetadata): boolean {
  if (menu.fileType === 'application/pdf') return true;
  const name = menu.fileName || menu.url || '';
  return /\.pdf(\?|#|$)/i.test(name);
}

export function isImageMenu(menu: FoodCourtMenuMetadata): boolean {
  if (menu.fileType?.startsWith('image/')) return true;
  const name = menu.fileName || menu.url || '';
  return /\.(png|jpe?g|webp)(\?|#|$)/i.test(name);
}

/** Resolve menu from content metadata (local/S3 key, menu object, or external PDF/image URL). */
export function getFoodCourtMenu(metadata: Record<string, unknown> | null | undefined): FoodCourtMenuMetadata | null {
  if (!metadata) return null;

  const menu = metadata.menu as FoodCourtMenuMetadata | undefined;
  if (menu && (menu.key || menu.url)) {
    return menu;
  }

  if (typeof metadata.menuKey === 'string' && metadata.menuKey) {
    return {
      key: metadata.menuKey,
      fileName: typeof metadata.menuFileName === 'string' ? metadata.menuFileName : undefined,
      fileType: typeof metadata.menuFileType === 'string' ? metadata.menuFileType : undefined,
    };
  }

  const externalUrl =
    (typeof metadata.menuUrl === 'string' && metadata.menuUrl) ||
    (typeof metadata.website === 'string' && MENU_URL_PATTERN.test(metadata.website) ? metadata.website : null);

  if (externalUrl) {
    const fileName = externalUrl.split('/').pop()?.split('?')[0] || 'menu';
    return { url: externalUrl, fileName };
  }

  return null;
}

export function hasFoodCourtMenu(metadata: Record<string, unknown> | null | undefined): boolean {
  return getFoodCourtMenu(metadata) !== null;
}
